import { useMutation } from '@apollo/client/react';
import { useAtomValue } from 'jotai';
import { useCallback, useRef, useState } from 'react';

import {
  GetPatientFollowupCommentsDocument,
  UpdateGuestPatientFollowupCommentDocument,
} from '~/graphql/generated/graphql';
import {
  patientDetailDialogPatientCommentState,
  patientDetailDialogState,
} from '~/state/partials/patient_detail_dialog/atoms';

import { FollowupCommentForm } from '.';
import { Fields } from './types';

export const useUpdateGuestPatientComment = () => {
  const formRef = useRef<RefAttributeType<typeof FollowupCommentForm> | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const patientDetailCommentState = useAtomValue(patientDetailDialogPatientCommentState);
  const patientDetailState = useAtomValue(patientDetailDialogState);

  const [updateGuestPatientComment] = useMutation(UpdateGuestPatientFollowupCommentDocument, {
    refetchQueries: [
      {
        query: GetPatientFollowupCommentsDocument,
        variables: {
          id: patientDetailState.patientId,
        },
      },
    ],
  });

  const update = useCallback(
    async (values: Fields) => {
      if (patientDetailCommentState.selectedCommentId === null) return;
      setIsUpdating(true);

      try {
        const res = await updateGuestPatientComment({
          variables: {
            input: {
              guestPatientMedicationFollowupCommentId: patientDetailCommentState.selectedCommentId,
              comment: values.comment,
              medicationFollowupStatus: null,
            },
          },
        });
        setError(null);
        setIsUpdating(false);

        return res.data?.updateGuestPatientFollowupComment?.guestPatientMedicationFollowupComment;
      } catch (_error) {
        setError(_error?.message || 'エラーが発生しました');
        setIsUpdating(false);
        throw new Error('', { cause: _error });
      }
    },
    [patientDetailCommentState.selectedCommentId, updateGuestPatientComment],
  );

  return {
    update,
    formRef,
    error,
    isUpdating,
  };
};
